import { useEffect, useState } from 'react';
import { useDaten } from './daten/kontext';
import type { Id, Datum } from './daten/typen';
import { Planung } from './ansichten/Planung';
import { Absagen } from './ansichten/Absagen';
import { Kurse } from './ansichten/Kurse';
import { Dozenten } from './ansichten/Dozenten';
import { Stammdaten } from './ansichten/Stammdaten';
import { Icon, Knopf, Meldungen, RueckfrageDialog } from './ui/bausteine';
import { montag } from './daten/zeit';
import { Simulation } from './simulation/Simulation';

type Seite = 'planung' | 'absagen' | 'kurse' | 'dozenten' | 'stammdaten';

function leseTouch(): boolean {
  try {
    const p = new URLSearchParams(location.search).get('touch');
    if (p !== null) return p === '1';
    return localStorage.getItem('mm-touch') === '1';
  } catch { return false; }
}

export function App({ abmelden }: { abmelden?: () => void }) {
  const { stand, quelle, ausfuehren, fragen } = useDaten();
  const [seite, setSeite] = useState<Seite>(() => {
    try { const s = new URLSearchParams(location.search).get('seite'); return (['absagen', 'kurse', 'dozenten', 'stammdaten'].includes(s ?? '') ? s : 'planung') as Seite; } catch { return 'planung'; }
  });
  const [touch, setTouch] = useState(leseTouch);
  const [simOffen, setSimOffen] = useState(() => {
    if (quelle.art !== 'demo') return false;
    try { if (localStorage.getItem('mm-sim-gesehen')) return false; localStorage.setItem('mm-sim-gesehen', '1'); } catch { /* egal */ }
    return true;
  });
  const [sprung, setSprung] = useState<{ woche: Datum; einsatz?: Id; n: number } | null>(null);

  useEffect(() => {
    document.documentElement.dataset.touch = touch ? '1' : '0';
    try { localStorage.setItem('mm-touch', touch ? '1' : '0'); } catch { /* egal */ }
  }, [touch]);
  useEffect(() => { document.documentElement.style.setProperty('--mandant', stand.mandant.farbe_primaer); }, [stand.mandant.farbe_primaer]);

  const offen = stand.absagen.filter(a => !a.erledigt_am).length;
  const NAV: { id: Seite; text: string; icon: string; zahl?: number }[] = [
    { id: 'planung', text: 'Planung', icon: 'kalender' },
    { id: 'absagen', text: 'Absagen', icon: 'glocke', zahl: offen },
    { id: 'kurse', text: 'Kurse', icon: 'liste' },
    { id: 'dozenten', text: 'Dozenten', icon: 'person' },
    { id: 'stammdaten', text: 'Stammdaten', icon: 'stift' },
  ];

  return (
    <div className="app">
      <header className="kopfzeile">
        <div className="marke">
          <span className="marke-zeichen" aria-hidden="true">M</span>
          <span className="marke-text"><strong>MeisterMind</strong><span>Planung</span></span>
          <span className="marke-trenner" aria-hidden="true" />
          {stand.mandant.logo_url ? <img src={stand.mandant.logo_url} alt={stand.mandant.name} className="mandant-logo" />
            : <span className="mandant-name">{stand.mandant.name}</span>}
        </div>
        <nav className="hauptnav" aria-label="Hauptnavigation">
          {NAV.map(n => (
            <button key={n.id} type="button" className={seite === n.id ? 'an' : ''} aria-current={seite === n.id ? 'page' : undefined} onClick={() => { setSprung(null); setSeite(n.id); }}>
              <Icon name={n.icon} /><span>{n.text}</span>{n.zahl ? <span className="zahl">{n.zahl}</span> : null}
            </button>
          ))}
        </nav>
        <div className="kopf-rechts">
          {quelle.art === 'demo' && <span className="demo-marke" title="Alle Daten liegen nur in diesem Browser">Demo</span>}
          {quelle.art === 'demo' && <Knopf art={simOffen ? 'primaer' : 'leise'} icon="zauber" onClick={() => setSimOffen(!simOffen)} title="Geführte Übungen an Beispieldaten">Übungen</Knopf>}
          {quelle.art === 'demo' && <Knopf art="leise" icon="person" onClick={() => { location.hash = 'dozent'; }} title="Die Demo aus Sicht eines Dozenten ansehen">Dozenten-App</Knopf>}
          <Knopf art={touch ? 'primaer' : 'leise'} icon="hand" aria-pressed={touch} onClick={() => setTouch(!touch)} title="Große Bedienelemente für Touch-Bildschirme">Touch</Knopf>
          {quelle.art === 'demo' && quelle.zuruecksetzen && <Knopf art="leise" icon="zurueck" title="Demo-Daten zurücksetzen"
            onClick={async () => { if (await fragen('Alle Änderungen verwerfen und die Demo-Daten wiederherstellen?', 'Zurücksetzen', true)) await ausfuehren(() => quelle.zuruecksetzen!(), 'Demo zurückgesetzt'); }} />}
          {abmelden && <Knopf art="leise" onClick={abmelden}>Abmelden</Knopf>}
        </div>
      </header>
      <main className="hauptbereich">
        {seite === 'planung' && <Planung key={sprung ? `s${sprung.n}` : 'p'} startWoche={sprung?.woche} startEinsatz={sprung?.einsatz} />}
        {seite === 'absagen' && <Absagen zuEinsatz={(datum, id) => { setSprung({ woche: montag(datum), einsatz: id, n: Date.now() }); setSeite('planung'); }} />}
        {seite === 'kurse' && <Kurse zurPlanung={w => { setSprung({ woche: w, n: Date.now() }); setSeite('planung'); }} />}
        {seite === 'dozenten' && <Dozenten />}
        {seite === 'stammdaten' && <Stammdaten />}
      </main>
      <Meldungen />
      <RueckfrageDialog />
      {quelle.art === 'demo' && <Simulation offen={simOffen} schliessen={() => setSimOffen(false)} />}
    </div>
  );
}
